import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCheck, Trash2, Package, ShoppingCart, CreditCard, UserCog, RotateCcw, TrendingDown, Truck, Wallet, UserPlus } from 'lucide-react';
import api from '../../services/api';

// Icône par type de notification.
const ICONES = {
  stock: Package,
  reappro: Package,
  appro: Truck,
  commande: ShoppingCart,
  retour: RotateCcw,
  perte: TrendingDown,
  dette: Wallet,
  abonnement: CreditCard,
  vendeur: UserCog,
  gerant: UserCog,
  utilisateur: UserPlus,
};

function tempsRelatif(date) {
  const d = new Date(date);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return d.toLocaleDateString('fr-FR');
}

const couleurNiveau = (n) =>
  n === 'alerte' ? 'text-red-400' : n === 'succes' ? 'text-green-400' : 'text-yellow-400';

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const chargerCompteur = useCallback(() => {
    api.get('/notifications/compteur')
      .then((r) => setCount(r.data?.non_lus || 0))
      .catch(() => {});
  }, []);

  // Compteur au montage + rafraîchissement toutes les 60 s.
  useEffect(() => {
    chargerCompteur();
    const id = setInterval(chargerCompteur, 60000);
    return () => clearInterval(id);
  }, [chargerCompteur]);

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const basculer = async () => {
    const ouvrir = !open;
    setOpen(ouvrir);
    if (ouvrir) {
      setLoading(true);
      try {
        const { data } = await api.get('/notifications');
        setItems(data?.notifications || []);
        setCount(data?.non_lus || 0);
      } catch { /* ignore */ } finally { setLoading(false); }
    }
  };

  const ouvrirNotif = async (notif) => {
    if (!notif.lu) {
      try { await api.post(`/notifications/${notif.id}/lu`); } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => (x.id === notif.id ? { ...x, lu: true } : x)));
      setCount((c) => Math.max(0, c - 1));
    }
    setOpen(false);
    if (notif.lien) navigate(notif.lien);
  };

  const toutLu = async () => {
    try { await api.post('/notifications/tout-lu'); } catch { /* ignore */ }
    setItems((prev) => prev.map((x) => ({ ...x, lu: true })));
    setCount(0);
  };

  const effacerTout = async () => {
    try { await api.delete('/notifications/tout'); } catch { /* ignore */ }
    setItems([]);
    setCount(0);
  };

  const effacerUne = async (e, notif) => {
    e.stopPropagation();
    try { await api.delete(`/notifications/${notif.id}`); } catch { /* ignore */ }
    setItems((prev) => prev.filter((x) => x.id !== notif.id));
    if (!notif.lu) setCount((c) => Math.max(0, c - 1));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={basculer}
        className="relative p-2 rounded-lg hover:bg-white/10 text-gray-200 transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell size={20} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[90vw] max-h-[70vh] flex flex-col bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <span className="font-semibold text-white text-sm">Notifications</span>
            <div className="flex items-center gap-2">
              {items.some((i) => !i.lu) && (
                <button onClick={toutLu} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1">
                  <CheckCheck size={14} /> Tout lu
                </button>
              )}
              {items.length > 0 && (
                <button onClick={effacerTout} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1" title="Effacer toutes les notifications">
                  <Trash2 size={14} /> Effacer
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-white p-1" aria-label="Fermer">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-center text-gray-500 text-sm py-8">Chargement…</p>
            ) : items.length === 0 ? (
              <p className="text-center text-gray-500 text-sm py-8">Aucune notification</p>
            ) : (
              items.map((n) => {
                const Icone = ICONES[n.type] || Bell;
                return (
                  <div
                    key={n.id}
                    onClick={() => ouvrirNotif(n)}
                    className={`w-full text-left flex gap-3 px-4 py-3 border-b border-gray-800/60 hover:bg-gray-800/50 transition-colors cursor-pointer ${n.lu ? 'opacity-60' : ''}`}
                  >
                    <Icone size={18} className={`shrink-0 mt-0.5 ${couleurNiveau(n.niveau)}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {!n.lu && <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />}
                        <p className="text-sm text-white font-medium truncate">{n.titre}</p>
                      </div>
                      {n.message && <p className="text-xs text-gray-400 mt-0.5">{n.message}</p>}
                      <p className="text-[11px] text-gray-500 mt-1">{tempsRelatif(n.created_at)}</p>
                    </div>
                    <button
                      onClick={(e) => effacerUne(e, n)}
                      className="shrink-0 self-start p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                      aria-label="Effacer cette notification"
                      title="Effacer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
